
import React, { useState } from 'react';
import { X, Send, Loader2, CheckCircle2, Smartphone, ExternalLink, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { createTesterRequest } from '../services/firebaseService';
import { AppUser } from '../types';

interface TesterApplicationModalProps {
    user: AppUser;
    onClose: () => void;
    androidGuideOnly?: boolean;
}

const TesterApplicationModal: React.FC<TesterApplicationModalProps> = ({ user, onClose, androidGuideOnly }) => {
    const { t } = useTranslation();
    const [motivation, setMotivation] = useState('');
    const [expectationsAccepted, setExpectationsAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!motivation.trim() || !expectationsAccepted) return;

        setIsSubmitting(true);
        setError(null);
        try {
            await createTesterRequest(user, motivation);
            setIsSuccess(true);
            setTimeout(onClose, 5000); // Increased timeout to let them see the download info
        } catch (err) {
            setError(t('upgrade.tester.apply.error_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed left-0 right-0 bottom-0 z-[10020] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-[var(--bg-base)] border border-[var(--border-secondary)] rounded-panel shadow-3xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">

                <div className="p-6 border-b border-[var(--border-primary)] flex justify-between items-center bg-[var(--bg-input)]">
                    <h2 className="text-xl font-black text-[var(--text-primary)] uppercase tracking-tight">{androidGuideOnly ? t('profile.android_app.title') : t('upgrade.tester.apply.title')}</h2>
                    <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-surface-alt)] rounded-xl">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 sm:p-8 space-y-6 overflow-y-auto max-h-[70vh]">
                    {(isSuccess || androidGuideOnly) ? (
                        <div className="flex flex-col items-center justify-center py-10 space-y-6 text-center animate-in fade-in">
                            {!androidGuideOnly && (
                                <div className="flex flex-col items-center space-y-4">
                                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500">
                                        <CheckCircle2 size={32} />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-lg font-black text-[var(--text-primary)] uppercase">{t('upgrade.tester.apply.success_title')}</h3>
                                        <p className="text-sm text-[var(--text-secondary)]">{t('upgrade.tester.apply.success_description')}</p>
                                    </div>
                                </div>
                            )}

                            {!Capacitor.isNativePlatform() && (
                                <div className="w-full space-y-3 text-left">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                                            <Smartphone size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-[13px] font-black text-[var(--text-primary)] uppercase tracking-tight">{t('profile.android_app.title')}</h3>
                                        </div>
                                    </div>

                                    <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
                                        {t('profile.android_app.desc')}
                                    </p>

                                    {/* Stable card — primary */}
                                    <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 rounded-2xl space-y-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <h4 className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight">{t('profile.android_app.stable_title')}</h4>
                                            <span className="text-3xs font-black text-emerald-300 uppercase tracking-widest bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full">{t('profile.android_app.stable_recommended')}</span>
                                        </div>
                                        <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
                                            {t('profile.android_app.stable_desc')}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => window.open(t('profile.android_app.play_store_link'), '_blank')}
                                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 transition-all active:scale-95 text-center"
                                        >
                                            <Smartphone size={14} /> {t('profile.android_app.btn_download_play')}
                                        </button>
                                    </div>

                                    {/* Beta card — secondary */}
                                    <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-4 rounded-2xl space-y-3">
                                        <h4 className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-tight">{t('profile.android_app.beta_title')}</h4>
                                        <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
                                            {t('profile.android_app.beta_desc')}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => window.open(t('profile.android_app.group_link'), '_blank')}
                                            className="w-full bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 text-[var(--text-primary)] transition-all active:scale-95"
                                        >
                                            <ExternalLink size={14} /> {t('profile.android_app.btn_join_group')}
                                        </button>
                                    </div>

                                    <div className="bg-[var(--bg-input)] rounded-xl p-3 border border-[var(--border-primary)]">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <Info size={12} className="text-emerald-400" />
                                            <span className="text-2xs font-black text-emerald-300 uppercase tracking-widest">{t('profile.android_app.pwa_vs_native_title')}</span>
                                        </div>
                                        <p className="text-2xs text-[var(--text-secondary)] leading-relaxed italic">
                                            {t('profile.android_app.pwa_vs_native_desc')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="bg-amber-500/10 border border-amber-500/20 p-5 rounded-2xl">
                                <p className="text-xs text-amber-200 leading-relaxed">
                                    {t('upgrade.tester.apply.tester_benefit')}
                                </p>
                            </div>

                            <div className="bg-blue-500/10 border border-blue-500/20 p-5 rounded-2xl space-y-3">
                                <h3 className="text-tiny font-black text-blue-300 uppercase tracking-widest">{t('upgrade.tester.apply.expectations_title')}</h3>
                                <ul className="space-y-2 text-[12px] text-[var(--text-primary)]">
                                    <li>{t('upgrade.tester.apply.expectation_1')}</li>
                                    <li>{t('upgrade.tester.apply.expectation_2')}</li>
                                    <li>{t('upgrade.tester.apply.expectation_3')}</li>
                                    <li>{t('upgrade.tester.apply.expectation_4')}</li>
                                </ul>
                                <label className="flex items-start gap-3 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={expectationsAccepted}
                                        onChange={(e) => setExpectationsAccepted(e.target.checked)}
                                        className="mt-0.5"
                                    />
                                    <span className="text-xs text-[var(--text-secondary)] leading-relaxed">
                                        {t('upgrade.tester.apply.accept_expectations')}
                                    </span>
                                </label>
                            </div>

                            <div className="space-y-2">
                                <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest pl-1">{t('upgrade.tester.apply.motivation_label')}</label>
                                <textarea
                                    value={motivation}
                                    onChange={(e) => setMotivation(e.target.value)}
                                    placeholder={t('upgrade.tester.apply.motivation_placeholder')}
                                    className="w-full h-40 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-sm text-[var(--text-primary)] focus:border-blue-500 outline-none resize-none leading-relaxed placeholder:text-[var(--text-muted)]"
                                    required
                                />
                            </div>

                            {error && (
                                <p className="text-xs text-red-400 font-bold bg-red-500/10 p-3 rounded-xl border border-red-500/20 text-center">
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting || !motivation.trim() || !expectationsAccepted}
                                className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                            >
                                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                {t('upgrade.tester.apply.btn_send')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TesterApplicationModal;
